/**
 * Store Locator
 * Handles geolocation, distance calculation, and store sorting
 */

class StoreLocator {
  constructor(container) {
    this.container = container;
    this.zipInput = container.querySelector('[data-zip-input]');
    this.zipDisplay = container.querySelector('[data-zip-display]');
    this.changeLink = container.querySelector('[data-change-zip]');
    this.storesContainer = container.querySelector('[data-stores-container]');
    this.storeItems = Array.from(container.querySelectorAll('[data-store-item]'));
    this.showMoreButton = container.querySelector('[data-show-more]');
    this.userLocation = null;
    this.userZipCode = null;
    this.stores = [];
    this.defaultZipText = this.zipDisplay?.dataset.defaultText || '';
    this.storageKey = 'storeLocatorZip';
    this.isShowingAll = false;
    this.storesToShow = parseInt(container.dataset.storesToShow) || 3;
    this.showMoreText = this.showMoreButton?.dataset.showMoreText || 'Show more';
    this.showLessText = this.showMoreButton?.dataset.showLessText || 'Show less';

    this.init();
  }

  init() {
    // Parse store data
    this.parseStores();
    
    // Set up event listeners
    this.setupEventListeners();
    this.setupDetailsAnimations();
    this.setupShowMoreButton();

    // Show default zip text if available
    if (this.defaultZipText) {
      this.setZipDisplayText(this.defaultZipText);
    }

    // Use saved postal code if available; otherwise fallback to geolocation
    try {
      const savedZip = sessionStorage.getItem(this.storageKey);
      if (savedZip) {
        this.applyZipCode(savedZip, { save: false });
        return;
      }
    } catch (error) {
      console.warn('Unable to access sessionStorage:', error);
    }

    // Get user location
    this.getUserLocation();
  }

  parseStores() {
    this.stores = this.storeItems.map((item) => {
      const geoLocation = item.dataset.geoLocation;
      let lat = null;
      let lng = null;

      if (geoLocation) {
        const coords = geoLocation.split(',').map(coord => parseFloat(coord.trim()));
        if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          lat = coords[0];
          lng = coords[1];
        }
      }

      return {
        element: item,
        storeId: item.dataset.storeId,
        companyNumber: item.dataset.companyNumber,
        name: item.dataset.storeName,
        city: item.dataset.city,
        country: item.dataset.country,
        address: item.dataset.address,
        postalCode: item.dataset.postalCode,
        shopUrl: item.dataset.shopUrl,
        webshopOnly: item.dataset.webshopOnly === 'true',
        directionsLink: item.dataset.directionsLink,
        geoLocation: geoLocation,
        lat: lat,
        lng: lng,
        stockQuantity: parseInt(item.dataset.stockQuantity) || 0,
        distance: null
      };
    });
  }

  setupEventListeners() {
    // Change zip code button
    if (this.changeLink) {
      this.changeLink.addEventListener('click', () => {
        this.showZipInput();
      });
    }

    // Zip code input
    if (this.zipInput) {
      this.zipInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleZipCodeChange();
        }
      });

      this.zipInput.addEventListener('blur', () => {
        if (this.zipInput.value.trim()) {
          this.handleZipCodeChange();
        }
      });
    }
  }

  showZipInput() {
    if (this.zipInput) {
      this.zipInput.style.display = 'block';
      this.zipInput.focus();
      if (this.zipDisplay) {
        this.zipDisplay.style.display = 'none';
      }
      if (this.changeLink) {
        this.changeLink.style.display = 'none';
      }
    }
  }

  showZipDisplay(zipCode) {
    this.setZipDisplayText(zipCode);
    if (this.zipInput) {
      this.zipInput.value = zipCode;
      this.zipInput.style.display = 'none';
    }
    if (this.changeLink) {
      this.changeLink.style.display = 'inline';
    }
  }

  hideZipInput() {
    if (this.zipInput) {
      this.zipInput.style.display = 'none';
      if (this.zipDisplay) {
        this.zipDisplay.style.display = 'inline';
      }
      if (this.changeLink) {
        this.changeLink.style.display = 'inline';
      }
    }
  }

  setZipDisplayText(text) {
    if (this.zipDisplay) {
      this.zipDisplay.textContent = text || this.defaultZipText || '';
      this.zipDisplay.style.display = 'inline';
    }
  }

  async applyZipCode(zipCode, { save = true } = {}) {
    if (!zipCode) return;

    // Close all details immediately without animation to avoid state issues
    this.closeAllDetails({ animate: false });

    this.userZipCode = zipCode;
    this.showZipDisplay(zipCode);

    if (save) {
      try {
        sessionStorage.setItem(this.storageKey, zipCode);
      } catch (error) {
        console.warn('Unable to save postal code:', error);
      }
    }

    try {
      const coords = await this.getCoordinatesFromPostalCode(zipCode);
      if (coords) {
        this.userLocation = coords;
        this.calculateDistances();
        this.sortAndDisplayStores();
      } else {
        this.displayStores();
      }
    } catch (error) {
      console.warn('Error applying postal code:', error);
      this.displayStores();
    }
  }

  setupDetailsAnimations() {
    this.detailsElements = Array.from(this.container.querySelectorAll('[data-store-details]'));

    this.detailsElements.forEach((details) => {
      const summary = details.querySelector('summary');
      const body = details.querySelector('[data-store-body]');

      if (!summary || !body) return;

      if (!summary.dataset.storeLocatorInit) {
        summary.dataset.storeLocatorInit = 'true';
        summary.setAttribute('role', 'button');
        summary.setAttribute('tabindex', '0');
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

        summary.addEventListener('click', (event) => {
          const interactiveTarget = event.target.closest('a, button');
          if (interactiveTarget) {
            return;
          }
          event.preventDefault();
          this.toggleDetails(details, body);
        });

        summary.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleDetails(details, body);
          }
        });
      } else {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      }

      if (details.open) {
        body.style.height = 'auto';
        body.style.opacity = 1;
        body.style.transform = 'translateY(0)';
        details.classList.add('is-open');
      }
    });
  }

  closeAllDetails({ animate = true } = {}) {
    if (!this.detailsElements) return;

    this.detailsElements.forEach((details) => {
      const body = details.querySelector('[data-store-body]');
      if (!body) return;

      if (!details.open && !details.classList.contains('is-open')) return;

      if (animate) {
        this.animateClose(details, body);
      } else {
        details.open = false;
        details.classList.remove('is-open');
        body.style.height = '0px';
        body.style.opacity = 0;
        body.style.transform = 'translateY(-8px)';
        const summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }
    });
  }

  toggleDetails(details, body) {
    if (details.hasAttribute('data-animating')) return;

    if (details.open) {
      this.animateClose(details, body);
    } else {
      this.animateOpen(details, body);
    }
  }

  animateOpen(details, body) {
    details.setAttribute('data-animating', 'true');
    details.open = true;
    details.classList.add('is-open');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');

    body.style.height = '0px';
    body.style.opacity = 0;
    body.style.transform = 'translateY(-8px)';

    requestAnimationFrame(() => {
      body.style.height = `${body.scrollHeight}px`;
      body.style.opacity = 1;
      body.style.transform = 'translateY(0)';
    });

    let transitionEnded = false;
    const cleanup = () => {
      if (transitionEnded) return;
      transitionEnded = true;
      body.style.height = 'auto';
      details.removeAttribute('data-animating');
      body.removeEventListener('transitionend', onTransitionEnd);
    };

    const onTransitionEnd = (event) => {
      if (event.target !== body) return;
      cleanup();
    };

    body.addEventListener('transitionend', onTransitionEnd);
    // Fallback timeout in case transitionend doesn't fire
    setTimeout(cleanup, 500);
  }

  animateClose(details, body) {
    details.setAttribute('data-animating', 'true');
    details.classList.remove('is-open');
    const startHeight = body.scrollHeight;
    body.style.height = `${startHeight}px`;

    requestAnimationFrame(() => {
      body.style.height = '0px';
      body.style.opacity = 0;
      body.style.transform = 'translateY(-8px)';
    });

    let transitionEnded = false;
    const cleanup = () => {
      if (transitionEnded) return;
      transitionEnded = true;
      details.open = false;
      body.style.height = '0px';
      details.removeAttribute('data-animating');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      body.removeEventListener('transitionend', onTransitionEnd);
    };

    const onTransitionEnd = (event) => {
      if (event.target !== body) return;
      cleanup();
    };

    body.addEventListener('transitionend', onTransitionEnd);
    // Fallback timeout in case transitionend doesn't fire
    setTimeout(cleanup, 500);
  }

  async getUserLocation() {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      this.setZipDisplayText(this.defaultZipText);
      this.displayStores();
      return;
    }

    try {
      const position = await this.getCurrentPosition();
      this.userLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      // Get postal code from coordinates
      await this.getPostalCodeFromCoordinates(this.userLocation.lat, this.userLocation.lng);
      
      // Calculate distances and sort
      this.calculateDistances();
      this.sortAndDisplayStores();
    } catch (error) {
      console.warn('Error getting user location:', error);
      this.setZipDisplayText(this.defaultZipText);
      // If geolocation fails, still show stores (unsorted)
      this.displayStores();
    }
  }

  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });
  }

  async getPostalCodeFromCoordinates(lat, lng) {
    try {
      // Use a reverse geocoding service
      // Note: This is a free service, but you may want to use a more reliable one
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'StoreLocator/1.0'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const postalCode = data.address?.postcode;
        
        if (postalCode) {
          this.userZipCode = postalCode;
          this.showZipDisplay(postalCode);
          try {
            sessionStorage.setItem(this.storageKey, postalCode);
          } catch (error) {
            console.warn('Unable to save postal code:', error);
          }
        }
      }
    } catch (error) {
      console.warn('Error getting postal code from coordinates:', error);
    }
  }

  async handleZipCodeChange() {
    const zipCode = this.zipInput?.value.trim();
    
    if (!zipCode) {
      return;
    }

    this.hideZipInput();
    await this.applyZipCode(zipCode);
  }

  async getCoordinatesFromPostalCode(postalCode) {
    try {
      // Use a geocoding service to get coordinates from postal code
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(postalCode)}&limit=1`,
        {
          headers: {
            'User-Agent': 'StoreLocator/1.0'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          return {
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon)
          };
        }
      }
    } catch (error) {
      console.warn('Error getting coordinates from postal code:', error);
    }
    return null;
  }

  calculateDistances() {
    if (!this.userLocation) {
      return;
    }

    this.stores.forEach((store) => {
      if (store.lat !== null && store.lng !== null) {
        store.distance = this.calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          store.lat,
          store.lng
        );
      } else {
        store.distance = null;
      }
    });
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    // Haversine formula to calculate distance between two coordinates
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = this.deg2rad(lat2 - lat1);
    const dLng = this.deg2rad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return distance;
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  sortAndDisplayStores() {
    // Sort stores by distance (null distances go to the end)
    this.stores.sort((a, b) => {
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });

    this.displayStores();
  }

  displayStores() {
    // Reorder DOM elements based on sorted stores
    this.stores.forEach((store) => {
      this.storesContainer.appendChild(store.element);
    });
    
    // Clear any stuck animating states and reset details elements
    this.resetDetailsState();
    
    // Only reset if we're not currently showing all stores
    // This preserves the user's choice when stores are re-sorted
    if (!this.isShowingAll) {
      // Handle show more/less functionality
      this.updateShowMoreVisibility();
    } else {
      // If showing all, just ensure all stores are visible
      this.stores.forEach((store) => {
        store.element.style.display = '';
        store.element.style.maxHeight = '';
        store.element.style.opacity = '';
        store.element.style.overflow = '';
      });
      // Update button text
      if (this.showMoreButton) {
        this.showMoreButton.textContent = this.showLessText;
        this.showMoreButton.style.display = 'block';
      }
      // Update last visible store
      this.updateLastVisibleStore();
    }
  }

  resetDetailsState() {
    // Refresh the detailsElements array after reordering
    this.detailsElements = Array.from(this.container.querySelectorAll('[data-store-details]'));
    
    // Clear any stuck animating states and ensure clean state for all details
    this.detailsElements.forEach((details) => {
      // Remove animating attribute that might be stuck
      details.removeAttribute('data-animating');
      
      const body = details.querySelector('[data-store-body]');
      if (body) {
        // Reset body styles to closed state
        if (!details.open) {
          body.style.height = '0px';
          body.style.opacity = '0';
          body.style.transform = 'translateY(-8px)';
        }
      }
    });
  }

  setupShowMoreButton() {
    if (!this.showMoreButton) return;
    
    this.showMoreButton.addEventListener('click', () => {
      this.toggleShowMore();
    });
    
    this.updateShowMoreVisibility();
  }

  updateShowMoreVisibility() {
    if (!this.showMoreButton) return;
    
    if (this.stores.length > this.storesToShow) {
      this.showMoreButton.style.display = 'block';
      this.applyShowMoreState();
    } else {
      this.showMoreButton.style.display = 'none';
    }
  }

  applyShowMoreState() {
    this.stores.forEach((store, index) => {
      // Remove last-visible class from all stores first
      store.element.classList.remove('is-last-visible');
      
      if (index < this.storesToShow) {
        // Always show first N stores
        store.element.style.display = '';
        store.element.style.maxHeight = '';
        store.element.style.opacity = '';
        store.element.style.overflow = '';
      } else {
        // Hide stores beyond first N if not showing all
        if (!this.isShowingAll) {
          store.element.style.display = 'none';
        } else {
          store.element.style.display = '';
          store.element.style.maxHeight = '';
          store.element.style.opacity = '';
          store.element.style.overflow = '';
        }
      }
    });
    
    // Mark the last visible store
    this.updateLastVisibleStore();
    
    // Update button text
    if (this.showMoreButton) {
      this.showMoreButton.textContent = this.isShowingAll ? this.showLessText : this.showMoreText;
    }
  }

  updateLastVisibleStore() {
    // Remove the class from all stores first
    this.stores.forEach((store) => {
      store.element.classList.remove('is-last-visible');
    });
    
    // Find the last visible store
    let lastVisibleStore = null;
    for (let i = this.stores.length - 1; i >= 0; i--) {
      const store = this.stores[i];
      const isVisible = store.element.style.display !== 'none' && 
                       window.getComputedStyle(store.element).display !== 'none';
      if (isVisible) {
        lastVisibleStore = store;
        break;
      }
    }
    
    // Add the class to the last visible store
    if (lastVisibleStore) {
      lastVisibleStore.element.classList.add('is-last-visible');
    }
  }

  toggleShowMore() {
    this.isShowingAll = !this.isShowingAll;
    
    if (this.isShowingAll) {
      this.showAllStores();
    } else {
      this.hideExtraStores();
    }
    
    if (this.showMoreButton) {
      this.showMoreButton.textContent = this.isShowingAll ? this.showLessText : this.showMoreText;
    }
  }

  showAllStores() {
    const extraStores = this.stores.slice(this.storesToShow);
    
    extraStores.forEach((store, index) => {
      const element = store.element;
      // First, make element visible but hidden to measure height
      element.style.display = '';
      element.style.maxHeight = 'none';
      element.style.opacity = '0';
      element.style.overflow = 'hidden';
      
      // Get the actual height while temporarily visible
      const height = element.scrollHeight;
      
      // Now set up for animation starting from 0
      element.style.maxHeight = '0';
      element.style.transition = 'max-height 0.45s ease, opacity 0.45s ease';
      
      // Force reflow
      void element.offsetHeight;
      
      requestAnimationFrame(() => {
        element.style.maxHeight = `${height}px`;
        element.style.opacity = '1';
      });
      
      const onTransitionEnd = (event) => {
        if (event.target !== element) return;
        element.style.maxHeight = '';
        element.style.overflow = '';
        element.style.display = '';
        element.style.opacity = '1';
        element.removeEventListener('transitionend', onTransitionEnd);
        // Update last visible store after all animations complete
        this.updateLastVisibleStore();
      };
      
      element.addEventListener('transitionend', onTransitionEnd);
    });
    
    // Also update immediately in case all animations are already done
    setTimeout(() => this.updateLastVisibleStore(), 500);
  }

  hideExtraStores() {
    const extraStores = this.stores.slice(this.storesToShow);
    
    extraStores.forEach((store) => {
      const element = store.element;
      const startHeight = element.scrollHeight;
      element.style.maxHeight = `${startHeight}px`;
      element.style.opacity = '1';
      element.style.overflow = 'hidden';
      element.style.transition = 'max-height 0.45s ease, opacity 0.45s ease';
      
      requestAnimationFrame(() => {
        element.style.maxHeight = '0';
        element.style.opacity = '0';
      });
      
      const onTransitionEnd = (event) => {
        if (event.target !== element) return;
        element.style.display = 'none';
        element.style.maxHeight = '';
        element.style.overflow = '';
        element.removeEventListener('transitionend', onTransitionEnd);
        // Update last visible store after all animations complete
        this.updateLastVisibleStore();
      };
      
      element.addEventListener('transitionend', onTransitionEnd);
    });
    
    // Also update immediately in case all animations are already done
    setTimeout(() => this.updateLastVisibleStore(), 500);
  }
}

// Initialize store locators when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const storeLocators = document.querySelectorAll('[data-store-locator]');
  storeLocators.forEach((container) => {
    new StoreLocator(container);
  });
});

// Also initialize for dynamic content (e.g., theme editor)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const storeLocators = document.querySelectorAll('[data-store-locator]');
    storeLocators.forEach((container) => {
      new StoreLocator(container);
    });
  });
} else {
  const storeLocators = document.querySelectorAll('[data-store-locator]');
  storeLocators.forEach((container) => {
    new StoreLocator(container);
  });
}

